import React, { useState, useMemo } from 'react';
import { AlignLeft, Copy, Check } from 'lucide-react';

interface Platform {
  name: string;
  limit: number;
  icon: string;
}

const PLATFORMS: Platform[] = [
  { name: 'Twitter/X', limit: 280, icon: 'X' },
  { name: 'Instagram Bio', limit: 150, icon: 'IG' },
  { name: 'SMS', limit: 160, icon: 'SMS' },
  { name: 'YouTube Title', limit: 100, icon: 'YT' },
  { name: 'Meta Description', limit: 160, icon: 'SEO' },
  { name: 'LinkedIn Summary', limit: 2000, icon: 'LI' },
  { name: 'Facebook Post', limit: 63206, icon: 'FB' },
];

const CharacterCounter: React.FC = () => {
  const [text, setText] = useState('');
  const [limit, setLimit] = useState(280);
  const [activePlatform, setActivePlatform] = useState('Twitter/X');
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    const characters = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const sentences = text.trim() ? (text.match(/[.!?]+/g) || []).length || (text.trim() ? 1 : 0) : 0;
    const spaces = (text.match(/\s/g) || []).length;
    const special = (text.match(/[^a-zA-Z0-9\s]/g) || []).length;
    const remaining = limit - characters;
    const percentage = limit > 0 ? Math.min(100, (characters / limit) * 100) : 0;

    return { characters, words, sentences, spaces, special, remaining, percentage };
  }, [text, limit]);

  const getProgressColor = (pct: number): string => {
    if (pct < 70) return 'bg-green-500';
    if (pct < 90) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTextColor = (pct: number): string => {
    if (pct < 70) return 'text-green-600';
    if (pct < 90) return 'text-yellow-600';
    return 'text-red-600';
  };

  const selectPlatform = (p: Platform) => {
    setActivePlatform(p.name);
    setLimit(p.limit);
  };

  const copyText = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isOverLimit = stats.characters > limit;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <AlignLeft className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Character Counter</h1>
          <p className="text-gray-500 text-sm">Count characters with platform-specific limits</p>
        </div>
      </div>

      {/* Platform presets */}
      <div className="mb-4">
        <label className="text-sm font-medium text-gray-700 block mb-2">Platform Limits</label>
        <div className="flex flex-wrap gap-2">
          {PLATFORMS.map(p => (
            <button
              key={p.name}
              onClick={() => selectPlatform(p)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors flex items-center gap-1.5 ${
                activePlatform === p.name
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
              }`}
            >
              <span className="font-bold text-xs">{p.icon}</span>
              <span className="hidden sm:inline">{p.name}</span>
              <span className="text-xs opacity-75">({p.limit})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom limit */}
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-gray-600">Custom limit:</label>
        <input
          type="number"
          value={limit}
          onChange={(e) => { setLimit(Number(e.target.value)); setActivePlatform(''); }}
          min={1}
          className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Character count display */}
      <div className="mb-3 flex items-end justify-between">
        <div className={`text-3xl font-bold ${getTextColor(stats.percentage)}`}>
          {stats.characters}
          <span className="text-lg text-gray-400 font-normal"> / {limit}</span>
        </div>
        <div className={`text-sm font-medium ${isOverLimit ? 'text-red-600' : 'text-gray-500'}`}>
          {isOverLimit
            ? `${Math.abs(stats.remaining)} over limit!`
            : `${stats.remaining} remaining`
          }
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div
          className={`h-3 rounded-full transition-all duration-200 ${getProgressColor(stats.percentage)}`}
          style={{ width: `${Math.min(100, stats.percentage)}%` }}
        />
      </div>

      {/* Warning */}
      {isOverLimit && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Your text exceeds the {limit}-character limit by <strong>{Math.abs(stats.remaining)}</strong> characters.
          {activePlatform && ` This content will be truncated on ${activePlatform}.`}
        </div>
      )}

      {/* Textarea */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Type or paste your text here..."
        className={`w-full h-48 p-4 border rounded-lg resize-y focus:ring-2 focus:border-indigo-500 text-gray-800 ${
          isOverLimit ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'
        }`}
      />

      {/* Copy button */}
      <div className="mt-3 flex gap-2">
        <button
          onClick={copyText}
          disabled={!text}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy Text'}
        </button>
        <button
          onClick={() => setText('')}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
        >
          Clear
        </button>
      </div>

      {/* Stats grid */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Characters', value: stats.characters },
          { label: 'Words', value: stats.words },
          { label: 'Sentences', value: stats.sentences },
          { label: 'Spaces', value: stats.spaces },
          { label: 'Special Chars', value: stats.special },
        ].map(({ label, value }) => (
          <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-800">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CharacterCounter;
